import React, { useState } from 'react'
import { router } from '@inertiajs/react'
import Layout from '../Layout'
import BackLink from '../../../Components/BackLink'
import Button from '../../../Components/Button'
import { formatPrice, getStatusClasses, capitalize, BOOKING_STATUS_COLORS } from '../../../utils/formatters'

function DetailRow({ label, children }) {
  return (
    <div className="flex justify-between py-2">
      <dt className="text-sm text-gray-500">{label}</dt>
      <dd className="text-sm font-medium text-gray-900 text-right">{children}</dd>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>
      <dl className="divide-y divide-gray-100">
        {children}
      </dl>
    </div>
  )
}

export default function Show({ account, accounts, navigation, booking }) {
  const [cancelReason, setCancelReason] = useState('')
  const [showCancelModal, setShowCancelModal] = useState(false)

  const formatBookingDate = (dateStr) => {
    if (!dateStr) return ''
    const date = new Date(dateStr + 'T00:00:00')
    return date.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
  }

  const handleStatusChange = (newStatus) => {
    if (confirm(`Change booking status to "${capitalize(newStatus.replace('_', ' '))}"?`)) {
      router.patch(`/manage/bookings/${booking.id}`, {
        booking: { status: newStatus }
      })
    }
  }

  const handleCancel = () => {
    router.patch(`/manage/bookings/${booking.id}`, {
      booking: { status: 'cancelled', cancellation_reason: cancelReason }
    }, {
      onSuccess: () => setShowCancelModal(false)
    })
  }

  return (
    <Layout
      account={account}
      accounts={accounts}
      navigation={navigation}
      title={`Booking ${booking.booking_reference}`}
    >
      <BackLink href="/manage/bookings">Back to Bookings</BackLink>

      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{booking.booking_reference}</h2>
            <p className="text-sm text-gray-500 mt-1">
              Source: {capitalize(booking.booking_source.replace('_', ' '))}
            </p>
          </div>
          <span className={`inline-flex rounded-full px-3 py-1 text-sm font-semibold ${getStatusClasses(booking.status, BOOKING_STATUS_COLORS)}`}>
            {capitalize(booking.status.replace('_', ' '))}
          </span>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-200">
          <Button href={`/manage/bookings/${booking.id}/edit`} variant="secondary" size="sm">
            Edit
          </Button>
          {booking.status === 'pending_payment' && (
            <Button onClick={() => handleStatusChange('confirmed')} size="sm">
              Confirm
            </Button>
          )}
          {booking.status === 'confirmed' && (
            <Button onClick={() => handleStatusChange('completed')} variant="secondary" size="sm">
              Mark Complete
            </Button>
          )}
          {booking.status === 'confirmed' && (
            <Button onClick={() => handleStatusChange('no_show')} variant="secondary" size="sm">
              Mark No-Show
            </Button>
          )}
          {booking.can_cancel && (
            <Button onClick={() => setShowCancelModal(true)} variant="danger" size="sm">
              Cancel Booking
            </Button>
          )}
        </div>
      </div>

      {/* Guest Details */}
      <Section title="Guest Details">
        <DetailRow label="Name">{booking.guest_name}</DetailRow>
        <DetailRow label="Email">
          <a href={`mailto:${booking.guest_email}`} className="text-sky-600 hover:text-sky-700">
            {booking.guest_email}
          </a>
        </DetailRow>
        {booking.guest_phone && (
          <DetailRow label="Phone">
            <a href={`tel:${booking.guest_phone}`} className="text-sky-600 hover:text-sky-700">
              {booking.guest_phone}
            </a>
          </DetailRow>
        )}
      </Section>

      {/* Reservation Details */}
      <Section title="Reservation Details">
        <DetailRow label="Date">{formatBookingDate(booking.date)}</DetailRow>
        <DetailRow label="Time">{booking.time}</DetailRow>
        <DetailRow label="Party Size">{booking.party_size} guests</DetailRow>
        {booking.venue_name && <DetailRow label="Venue">{booking.venue_name}</DetailRow>}
        {booking.package_name && <DetailRow label="Package">{booking.package_name}</DetailRow>}
      </Section>

      {/* Financials */}
      <Section title="Financials">
        <DetailRow label="Total">{formatPrice(booking.total_pence)}</DetailRow>
        {booking.deposit_pence > 0 && (
          <DetailRow label="Deposit">{formatPrice(booking.deposit_pence)}</DetailRow>
        )}
        <DetailRow label="Payment Status">
          {booking.paid_at ? `Paid on ${new Date(booking.paid_at).toLocaleDateString('en-GB')}` : 'Unpaid'}
        </DetailRow>
      </Section>

      {/* Notes */}
      {(booking.notes || booking.internal_notes) && (
        <Section title="Notes">
          {booking.notes && <DetailRow label="Guest Notes">{booking.notes}</DetailRow>}
          {booking.internal_notes && <DetailRow label="Internal Notes">{booking.internal_notes}</DetailRow>}
        </Section>
      )}

      {/* Cancellation */}
      {booking.status === 'cancelled' && (
        <Section title="Cancellation">
          {booking.cancelled_at && (
            <DetailRow label="Cancelled">
              {new Date(booking.cancelled_at).toLocaleDateString('en-GB')}
            </DetailRow>
          )}
          {booking.cancellation_reason && (
            <DetailRow label="Reason">{booking.cancellation_reason}</DetailRow>
          )}
        </Section>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Cancel Booking</h3>
            <p className="text-sm text-gray-600 mb-4">
              Are you sure you want to cancel booking {booking.booking_reference}?
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Reason for cancellation (optional)"
              className="w-full rounded-md border-gray-300 text-sm mb-4"
              rows={3}
            />
            <div className="flex justify-end gap-3">
              <Button variant="secondary" size="sm" onClick={() => setShowCancelModal(false)}>
                Keep Booking
              </Button>
              <Button variant="danger" size="sm" onClick={handleCancel}>
                Cancel Booking
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
